/**
 * web/src/hooks/mutations/useUpdateSlackUserId.ts
 *
 * Phase 6.2 SPN-17 — PATCH /api/admin/users/:id/slack-user-id.
 *
 * Optimistically patches the user row in the qk.adminAccess() cache so the
 * input value flips immediately. Rolls back on error and surfaces the
 * server's standard {error, code} message via a toast.
 *
 * Mirrors useToggleAccess but operates on the users[] array (not grants[]).
 */
import { patchUserSlackUserId } from '../../api/access';
import { useOptimisticMatrixMutation } from './_optimistic';

/** Body for the mutation. */
export interface UpdateSlackUserIdBody {
  userId: string;
  /** New Slack workspace member ID, or null to clear. */
  slackUserId: string | null;
}

/**
 * React Query mutation hook. Calls PATCH /api/admin/users/:id/slack-user-id
 * and patches the qk.adminAccess() cache optimistically via the shared
 * admin-matrix wrapper (F4).
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP PATCH, optimistic cache mutation, toast on success/error
 * @idempotent yes — same body lands the field at the same final state
 */
export function useUpdateSlackUserId() {
  return useOptimisticMatrixMutation<UpdateSlackUserIdBody>(
    (body) => patchUserSlackUserId(body.userId, body.slackUserId),
    (prev, body) => ({
      ...prev,
      users: prev.users.map((u) =>
        u.id === body.userId ? { ...u, slackUserId: body.slackUserId } : u,
      ),
    }),
    { errorFallback: 'Failed to save Slack User ID', successMessage: 'Slack User ID saved' },
  );
}
